import { QueryIntent } from '../../constants';
import type { IntentClassificationData, PersonIntent } from '../../query-intent-api/types';
import { NLPSearchResultFormat } from '../types';

import type { IntentSkipHandler, SkipAIAnswerResult } from './types';

// Type guards for intent classification
const isPersonIntent = (data: IntentClassificationData): data is PersonIntent =>
	data.classification === QueryIntent.PERSON;

/**
 * Registry mapping QueryIntent classification to skip handlers.
 * Add new handlers here to support additional intent types.
 */
const intentSkipHandlers: Partial<Record<QueryIntent, IntentSkipHandler>> = {
	/**
	 * PERSON intent: Skip API call if persons array has data.
	 * Transform Person[] from intent to NLPSearchPersonResultType format.
	 */
	[QueryIntent.PERSON]: (intentData): SkipAIAnswerResult => {
		if (
			!isPersonIntent(intentData) ||
			intentData.persons === undefined ||
			intentData.persons === null
		) {
			return { skip: false };
		}

		// Transform intent persons to the format expected by knowledge cards
		const mappedPersons = {
			user_metadata_only_cards: intentData.persons.map((person) => ({
				id: person.accountId,
				name: person.name,
				title: person.jobTitle || undefined,
				avatarUrl: person.picture || undefined,
				location: person.location || undefined,
				zoneInfo: person.zoneInfo || undefined,
			})),
		};

		return {
			skip: true,
			data: {
				nlpResult: JSON.stringify(mappedPersons),
				format: NLPSearchResultFormat.JSON,
				errorState: null,
				disclaimer: null,
				uniqueSources: [],
				extraAPIAnalyticsAttributes: {
					skippedViaIntentClassification: true,
					intentClassification: intentData.classification,
				},
			},
		};
	},
};

/**
 * Checks if we can skip the AI answer API call based on intent classification data.
 * If the intent already contains the data we need, returns it directly.
 */
export const checkSkipAIAnswerCall = (
	intentData: IntentClassificationData | undefined,
): SkipAIAnswerResult => {
	if (!intentData) {
		return { skip: false };
	}

	const handler = intentSkipHandlers[intentData.classification];
	if (!handler) {
		return { skip: false };
	}

	return handler(intentData);
};
